import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, runsOnDevice } from "../api/client.js";
import { nativeCatalogAvailable } from "../api/nativeCatalog.js";
import type { DiscoveryEntry } from "../api/nativeCatalog.js";
import { providerBody } from "../epg/api.js";
import { alternate } from "../discovery/discoveryPick.js";
import { useDiscoveryIndex } from "../discovery/discoveryFiles.js";
import { webDiscoveryRecommended } from "../discovery/webDiscovery.js";
import { useFavoritesStore, type FavoriteKind } from "../library/favoritesStore.js";
import { useLockedCategoryIds } from "../library/useCatalogQuery.js";
import { useProgressStore, watchProgressForProfile } from "../library/progressStore.js";
import { useVodCatalog } from "../library/vodHooks.js";
import { useProfiles } from "../settings/profileStore.js";

/**
 * Home's "Recommended For You" row (owner, 2026-10-12), which replaces My List there.
 *
 * It is PER PROFILE, never universal: what a profile watched (the progress store, by profile id)
 * and what it has on My List (favorites, by profile id) are the whole of its taste, and a Kids
 * profile sees only Kids-safe titles. The profile id is part of every query key and nothing is
 * kept across a profile switch (no placeholder data), so one profile's row can never show
 * another's. A fresh profile gets the top titles of each genre; as its history grows the row
 * follows its taste the way You May Also Like does (discovery/discoveryPick.ts recommended()).
 *
 * Films and series are worked out separately, on the device by the native catalog and on the
 * website in the page, then alternated into one row.
 */

export interface RecommendedItem {
  side: "vod" | "series";
  entry: DiscoveryEntry;
}

/** What the row needs from a profile. Exported so tests can pin the per-profile rule. */
export interface ProfileTaste {
  watchedIds: string[];
  favouriteFilmIds: string[];
  favouriteSeriesIds: string[];
  /** How many distinct titles in all, films and series together. */
  total: number;
}

export function profileTaste(
  progress: ReadonlyArray<{ profileId: string; contentType: string; contentRef: string }>,
  profileId: string,
  favouriteFilmIds: readonly string[],
  favouriteSeriesIds: readonly string[],
): ProfileTaste {
  // Films only: an episode does not carry its series, so a series' taste is My List alone.
  const watched = watchProgressForProfile(progress as never, profileId)
    .filter((entry) => entry.contentType === "vod")
    .map((entry) => entry.contentRef);
  const watchedIds = [...new Set(watched)];
  const films = new Set([...watchedIds, ...favouriteFilmIds]);
  const series = new Set(favouriteSeriesIds);
  return {
    watchedIds,
    favouriteFilmIds: [...favouriteFilmIds],
    favouriteSeriesIds: [...favouriteSeriesIds],
    total: films.size + series.size,
  };
}

/**
 * This profile's favorites of a kind, newest first. Read by the profile id the hook was given, not
 * by "whoever is active now", so a profile switch can never let one profile's My List into
 * another's row for even one render.
 */
function favoriteRefsOf(
  favorites: Record<string, { profileId: string; contentType: string; contentRef: string; addedAt: string }>,
  profileId: string,
  kind: FavoriteKind,
): string[] {
  return Object.values(favorites)
    .filter((row) => row.profileId === profileId && row.contentType === kind)
    .sort((a, b) => b.addedAt.localeCompare(a.addedAt))
    .map((row) => row.contentRef);
}

/**
 * The row's titles, in order. `undefined` while working it out; `null` when the device or website
 * has no discovery files yet (Home then keeps its My List row, so it is never poorer).
 */
export function useRecommended(
  sourceId: string,
  size: number,
  day: number,
  enabled: boolean,
): { items: RecommendedItem[] | null | undefined } {
  const profileId = useProfiles((s) => s.activeProfileId);
  const kids = useProfiles((s) => s.profiles.find((p) => p.id === s.activeProfileId)?.isKids === true);
  const progress = useProgressStore((s) => s.entries);
  const favorites = useFavoritesStore((s) => s.favorites);
  const favFilms = useMemo(() => favoriteRefsOf(favorites, profileId, "movie"), [favorites, profileId]);
  const favSeries = useMemo(() => favoriteRefsOf(favorites, profileId, "series"), [favorites, profileId]);
  const taste = useMemo(() => profileTaste(progress, profileId, favFilms, favSeries), [progress, profileId, favFilms, favSeries]);

  const web = !runsOnDevice();
  const device = runsOnDevice() && nativeCatalogAvailable();
  const on = enabled && Boolean(sourceId) && (web || device);

  // --- the device: the native catalog answers, one call a side ---
  const lockedFilms = useLockedCategoryIds(sourceId, "vod", on && device);
  const lockedSeries = useLockedCategoryIds(sourceId, "series", on && device);
  const ask = (kind: "vod" | "series", locked: string[] | undefined) => ({
    // The profile id leads the key: a switch can never reuse another profile's answer.
    queryKey: ["recommended", profileId, sourceId, kind, size, day, kids, taste.total, taste.watchedIds, taste.favouriteFilmIds, taste.favouriteSeriesIds, locked],
    queryFn: () =>
      api.post<DiscoveryEntry[] | null>(`/api/sources/${encodeURIComponent(sourceId)}/discovery/recommended`, {
        ...providerBody(),
        request: {
          kind,
          size,
          kids,
          tasteTotal: taste.total,
          day,
          watchedIds: taste.watchedIds,
          favouriteIds: kind === "vod" ? taste.favouriteFilmIds : taste.favouriteSeriesIds,
        },
        excludeCategories: locked,
      }),
    enabled: on && device && locked !== undefined,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const films = useQuery(ask("vod", lockedFilms));
  const series = useQuery(ask("series", lockedSeries));

  // --- the website: the page works it out over the catalog it holds ---
  const catalog = useVodCatalog(sourceId, on && web);
  const index = useDiscoveryIndex(on && web);
  const webItems = useMemo(() => {
    if (!on || !web) return undefined;
    if (index === null || catalog.isError) return null;
    if (index === undefined || !catalog.data) return undefined;
    const common = { size, kids, day, tasteTotal: taste.total, watchedIds: taste.watchedIds };
    const f = webDiscoveryRecommended(index, catalog.data.vod, { ...common, kind: "vod", favouriteIds: taste.favouriteFilmIds });
    const s = webDiscoveryRecommended(index, catalog.data.series, { ...common, kind: "series", favouriteIds: taste.favouriteSeriesIds });
    return alternate(
      f.map((entry): RecommendedItem => ({ side: "vod", entry })),
      s.map((entry): RecommendedItem => ({ side: "series", entry })),
      size,
    );
  }, [on, web, index, catalog.data, catalog.isError, size, day, kids, taste]);

  return useMemo(() => {
    if (!on) return { items: null };
    if (web) return { items: webItems };
    if (!device) return { items: null };
    if (films.isError || series.isError) return { items: null };
    if (films.data === undefined || series.data === undefined) return { items: undefined };
    if (films.data === null && series.data === null) return { items: null };
    return {
      items: alternate(
        (films.data ?? []).map((entry): RecommendedItem => ({ side: "vod", entry })),
        (series.data ?? []).map((entry): RecommendedItem => ({ side: "series", entry })),
        size,
      ),
    };
  }, [on, web, device, webItems, films.data, films.isError, series.data, series.isError, size]);
}
